import React, { useState, useContext, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useHistory } from "react-router-dom"
import Header from "../shared/Header/Header"
import BandsFeed from "../bands/BandsFeed"
import Heading from "../shared/Heading"
import style from "../shared/PageStyle.module.css"
import { isMobile } from "react-device-detect"
import { UserContext } from "../HomeApp"
import MakeStuff from "./components/MakeStuff"
import ActivityFeed from "./components/ActivityFeed"
import UserCalendar from "./components/UserCalendar"
import UserPlaylists from "./components/UserPlaylists"
import HotBits from "../HotBits"
import ShowOfTheWeekSidebar from "./components/ShowOfTheWeekSidebar"
import SellIt from "./components/SellIt"
import { openAuthDialog } from "../Authentication"
import ConcertFeed from "./components/ConcertFeed"
import SuggestiveAction from "../SuggestiveAction"
import { usePageStyleWithPlayer } from "../../util/usePageStyleWithPlayer"
import { useReferralCapture } from "@hooks/useReferralCapture"

const MIN_FOLLOWS_FOR_FOLLOWING = 3

export const HomeScreen = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)
  const initFormState = searchParams.get("make-stuff")
  const initShowDialog = searchParams.get("show-dialog")
  const [showForm, setShowForm] = useState(initFormState)
  const [newData, setNewData] = useState(null)
  const { user, scene } = useContext(UserContext)

  // Capture referral when arriving via a tracked link (e.g. a personal
  // "Share bandNada" link) — general/app-level share, no post involved.
  useReferralCapture()

  // Open auth dialog when URL has ?show-dialog (e.g. ?show-dialog=register)
  useEffect(() => {
    if (initShowDialog && !user) {
      const validStates = ["register", "reset_password", "auth"]
      const modalState = validStates.includes(initShowDialog) ? initShowDialog : "auth"
      dispatch(openAuthDialog({ modalState }))
    }
  }, [location.search, initShowDialog, dispatch, user])

  const postFilter = searchParams.get("filter") ?? ""
  const [feedType, setFeedType] = useState(
    user && user.user_bands_following > MIN_FOLLOWS_FOR_FOLLOWING
      ? "following"
      : "scene",
  ) // following, everything, scene

  // Sidebar vs. pitch-in-feed is a layout decision tied to available width,
  // not device type - isMobile (UA/touch sniffing from react-device-detect)
  // reports true for iPads regardless of orientation, which disagreed with
  // the lg: grid breakpoint below and broke the two-column layout for
  // logged-out users on iPad landscape.
  const [isDesktopWidth, setIsDesktopWidth] = useState(
    () => window.matchMedia("(min-width: 1024px)").matches,
  )
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)")
    const onChange = () => setIsDesktopWidth(mql.matches)
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [])
  const showPitchInFeed = !user && !isDesktopWidth
  const pageStyle = usePageStyleWithPlayer()

  const onFilterClick = (filter) => {
    const next = postFilter === filter ? "" : filter
    const params = new URLSearchParams(location.search)
    if (next) {
      params.set("filter", next)
    } else {
      params.delete("filter")
    }
    history.replace({ ...location, search: params.toString() })
  }

  const cheapShowsOnly = searchParams.get("cheap_shows") === "true"

  const onCheapShowsToggle = () => {
    const params = new URLSearchParams(location.search)
    if (cheapShowsOnly) {
      params.delete("cheap_shows")
    } else {
      params.set("cheap_shows", "true")
    }
    history.replace({ ...location, search: params.toString() })
  }

  return (
    <div
      className={style.rootElStyle}
      id="home-screen-container"
      style={pageStyle}
    >
      <Header user={user} />
      <div className="grid grid-cols-12 w-full pb-2 mt-4 px-3 lg:px-4">
        <SuggestiveAction />
        {!showPitchInFeed && (
          <div className="lg:col-span-4 col-span-12 lg:pr-6">
            {!user && !showPitchInFeed && <SellIt />}
            {user?.band ? (
              <>
                <div className="text-xs text-white uppercase leading-none">
                  Create Your Event or Post in bandNada
                </div>
                <MakeStuff />
              </>
            ) : (
              <div className="mt-3"></div>
            )}
            {user && <ShowOfTheWeekSidebar className="mb-6" />}

            {!(isMobile && showForm) && user && (
              <div className="mb-6">
                <UserCalendar user={user} />
              </div>
            )}
            {!(isMobile && showForm) && user && (
              <div className="mb-6">
                <UserPlaylists userId={user.id} />
              </div>
            )}
            {user?.enable_hot_bits && !isMobile && <HotBits />}
            {user?.enable_activity_feed && !isMobile && (
              <div className="mb-6">
                <ActivityFeed />
              </div>
            )}
          </div>
        )}

        <div className="lg:col-span-8 col-span-12">
          {!(isMobile && showForm) && (
            <Heading
              level={2}
              type="sectionHeader"
              className="lg:flex justify-between"
            >
              <div className="lg:text-base xl:text-2xl">
                <div className="text-xs text-black-800 uppercase leading-none">
                  Feed
                </div>
                {user && (
                  <>
                    <button
                      onClick={(e) => setFeedType("following")}
                      title="only content from the bands you follow"
                      className={`focus:outline-none ${
                        feedType === "following"
                          ? "text-black-800 font-bold uppercase"
                          : "text-gray-500 lowercase"
                      }`}
                    >
                      Following
                    </button>
                    <span className="px-2 text-gray-300">|</span>
                  </>
                )}
                <button
                  onClick={(e) => setFeedType("scene")}
                  title="only content from your current scene"
                  className={`focus:outline-none ${
                    feedType === "scene"
                      ? "text-black-800 font-bold uppercase"
                      : "text-gray-500 lowercase"
                  }`}
                >
                  {scene}
                </button>
                <span className="px-2 text-gray-300">|</span>
                <button
                  onClick={(e) => setFeedType("everything")}
                  title="everything (not recommended)"
                  className={`focus:outline-none ${
                    feedType === "everything"
                      ? "text-black-800 font-bold uppercase"
                      : "text-gray-500 lowercase"
                  }`}
                >
                  <span role="img" aria-label="everything (not recommended)">
                    ☠️
                  </span>
                </button>
              </div>

              <div className="flex items-end pb-1">
                <button
                  onClick={(e) => onFilterClick("")}
                  className="text-sm focus:outline-none mr-3"
                  style={{ color: postFilter === "" ? "black" : "gray" }}
                >
                  Everything
                </button>
                <button
                  onClick={(e) => onFilterClick("albums")}
                  className="text-sm focus:outline-none mr-3"
                  style={{ color: postFilter === "albums" ? "black" : "gray" }}
                >
                  Music
                </button>
                <button
                  onClick={(e) => onFilterClick("concerts")}
                  className="text-sm focus:outline-none mr-3"
                  style={{
                    color: postFilter === "concerts" ? "black" : "gray",
                  }}
                >
                  Shows
                </button>
                <button
                  onClick={(e) => onFilterClick("videos")}
                  className="text-sm focus:outline-none mr-3"
                  style={{ color: postFilter === "videos" ? "black" : "gray" }}
                >
                  Video
                </button>
                <button
                  onClick={(e) => onFilterClick("headlines")}
                  className="text-sm focus:outline-none mr-3"
                  style={{
                    color: postFilter === "headlines" ? "black" : "gray",
                  }}
                >
                  Posts
                </button>
                <button
                  onClick={(e) => onFilterClick("playlists")}
                  className="text-sm focus:outline-none"
                  style={{
                    color: postFilter === "playlists" ? "black" : "gray",
                  }}
                >
                  Playlists
                </button>
              </div>
            </Heading>
          )}

          {!(isMobile && showForm) && postFilter === "concerts" && (
            <div className="mb-3">
              <button
                onClick={onCheapShowsToggle}
                aria-pressed={cheapShowsOnly}
                className={`text-xs font-bold uppercase px-3 py-1 rounded border focus:outline-none ${
                  cheapShowsOnly
                    ? "bg-bn-neon-red border-bn-neon-red text-white"
                    : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                }`}
                style={
                  cheapShowsOnly
                    ? { boxShadow: "0 0 10px 2px rgba(255,99,99,0.6)" }
                    : undefined
                }
              >
                Cheap Shows
              </button>
            </div>
          )}

          {!(isMobile && showForm) ? (
            postFilter === "concerts" ? (
              <ConcertFeed
                newData={newData}
                feedType={feedType}
                cheapShowsOnly={cheapShowsOnly}
              />
            ) : (
              <BandsFeed
                showPitchInFeed={showPitchInFeed}
                filter={postFilter}
                feedType={feedType}
                newData={newData}
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default HomeScreen
